import { IntRange } from '@utils';
import { RedactionConfig } from './RedactionPolys';

export type RedactionType = 'blur' | 'black_fill' | 'outline' | 'inverse_blur';

export enum FILL_TYPE {
  BLUR = 'blur',
  BLACK_FILL = 'black_fill',
  OUTLINE = 'outline',
  INVERSE_BLUR = 'inverse_blur',
  MIXED_EFFECTS = 'mixedEffects',
}

export const FILL_TYPE_ORDER: Record<RedactionType, number> = {
  inverse_blur: 1,
  black_fill: 2,
  blur: 3,
  outline: 4,
};

export enum ClusterSimThresholdType {
  VERY_LOW = 'verylow',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  VERY_HIGH = 'veryhigh',
}

export const BlurEngineRedactionType = {
  blur: 'blur',
  black_fill: 'blackFill',
  outline: 'outline',
  inverse_blur: 'inverseBlur',
};

export type DetectionType =
  | 'head'
  | 'licensePlate'
  | 'laptop'
  | 'vehicle'
  | 'udr'
  | 'id card'
  | 'notepad';

export type ShapeType = 'rectangle' | 'ellipse';

export enum FeatherType {
  NONE = 'none',
  LIGHT = 'light',
  MEDIUM = 'medium',
  HEAVY = 'heavy',
}

export type BlurLevel = IntRange<1, 10>;

export interface LegacyGlobalSettings {
  readonly faceDetectionScaling: number; // 0 - 100
  readonly blurLevel?: BlurLevel; // integer 1-10 Legacy setting and won't be used in GlobalSettings. Will be removed when loading from these settings
  readonly blurLevelVehicle?: BlurLevel; // integer 1-10 Legacy setting and won't be used in GlobalSettings. Will be removed when loading from these settings
  readonly redactionType?: RedactionType; // Legacy setting and won't be used in GlobalSettings. Will be removed when loading from these settings
  // In legacyGlobalSettings, ObjectTypeEffects could contain blurLevel and blackFill if so, these will be moved to redactionConfig in saga.ts
  readonly objectTypeEffects: {
    readonly head: ObjectTypeEffects & LegacyObjectTypeEffects;
    readonly udr: ObjectTypeEffects & LegacyObjectTypeEffects;
    readonly laptop: ObjectTypeEffects & LegacyObjectTypeEffects;
    readonly vehicle: ObjectTypeEffects & LegacyObjectTypeEffects;
    readonly plate: ObjectTypeEffects & LegacyObjectTypeEffects;
    readonly notepad?: ObjectTypeEffects;
    readonly card?: ObjectTypeEffects;
    readonly person?: ObjectTypeEffects;
  };
}

export interface LegacyObjectTypeEffects {
  readonly blurLevel?: BlurLevel; // Moved to redactionConfig.blurLevel, contains the same value
  readonly blackFill?: boolean; // Moved to redactionConfig.redactionType, uses string of redactionType instead of true/false boolean blackFill
}

export interface ObjectTypeEffects {
  readonly shapeType: ShapeType;
  readonly redactionConfig: RedactionConfig;
  readonly scaling?: number; // 0 - 50
  // readonly featherType: FeatherType;
}

export interface GlobalSettings {
  readonly faceDetectionThreshold: number; // 0 - 100
  readonly videoType: 'Bodycam' | 'Broadcast' | 'CCTV';
  readonly detectionRate: number; // >= 1
  readonly autoInterpolationMax: number; // Default: 1000 (milliseconds)
  readonly manualInterpolationMax: number; // Default: 10_000? (milliseconds)
  readonly interpolationMaxStretch?: number; // max size ratio of interpolation gap relative to connected objects
  readonly trackingBackwardLimitSec: number; //  Max optical tracking backward (seconds)
  readonly trackingForwardLimitSec: number; //  Max optical tracking forward (seconds)
  readonly patchPreviousVersion: boolean;
  readonly showNonRedactedOverlays: boolean;
  readonly audioType: number;
  readonly audioToneLevel: number; // Default: 0.03  ||  0.01=low, 0.03=normal, 0.1=loud.
  readonly videoOffset: number;
  readonly objectTypeEffects: {
    readonly head: ObjectTypeEffects & { scaling: number }; // 0 - 50
    readonly udr: Omit<ObjectTypeEffects, 'scaling'>;
    readonly laptop: ObjectTypeEffects & { scaling: number }; // 0 - 50
    readonly vehicle: ObjectTypeEffects & { scaling: number }; // 0 - 50
    readonly plate: ObjectTypeEffects & { scaling: number }; // 0 - 50
    readonly notepad: ObjectTypeEffects & { scaling: number }; // 0 - 50
    readonly card: ObjectTypeEffects & { scaling: number }; // 0 - 50
    readonly person: ObjectTypeEffects & { scaling: number }; // 0 - 50
    /**
     * VE-30516: the Global Settings modal writes
     * `objectTypeEffects.background.redactionConfig.fillType`, so the key exists
     * in saved settings whether or not the type admitted it. Optional, because
     * settings saved before that control existed carry no such key, and not
     * scalable — there is no background box to grow.
     */
    readonly background?: Omit<ObjectTypeEffects, 'scaling'>;
  };
  readonly isSaved?: boolean; // whether has been saved to TDO
  /**
   * VE-25870: when the settings were last persisted. A settings change can make a
   * redacted output out of date just as an edit can, so the staleness check
   * compares this against the redaction data the output was built from. Absent on
   * TDOs whose settings were last saved before this field existed.
   */
  readonly lastSavedDateTime?: string;
  // Developer settings
  readonly legacyClustering?: boolean;
  readonly clusterSimThreshold?: ClusterSimThresholdType;
  readonly autoAddBackgroundBlur?: boolean;
  readonly defaultBackgroundBlurLevel?: BlurLevel;
  readonly backgroundRedactionFillType?: RedactionType; // only actually supports blur - may eventually support others such as blackfill
}

export interface BaseGlobalSettings
  extends LegacyGlobalSettings,
    // Note: Use LegacyGlobalSettings which contain LegacyObjectTypeEffects or ObjectTypeEffects
    Omit<GlobalSettings, 'objectTypeEffects'> {}
